// src/pages/ParabolicBreakouts.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import {
    QuickTradeEquityButton,
    QuickTradeOptionButton,
} from "../components/QuickTradeButtons";
import TickerWithName from "../components/TickerWithName"; // 🔥 NEW import

const money2 = (v) => {
    const n = Number(v || 0);
    return n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export default function ParabolicBreakouts() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);

    // NEW: universe + limit
    const [universe, setUniverse] = useState("popular"); // 'popular' | 'nyse' | 'custom'
    const [customTickers, setCustomTickers] = useState(""); // comma-separated
    const [limit, setLimit] = useState(20);

    const navigate = useNavigate();

    const buildUniverseParam = () => {
        if (universe === "custom") {
            const raw = customTickers.trim().replace(/\s+/g, "");
            // minimal validation: allow letters, numbers, commas, dots, dashes
            const ok = /^[A-Za-z0-9,.\-]+$/.test(raw || "");
            return ok && raw ? raw : "popular";
        }
        return universe;
    };

    const fetchBreakouts = async () => {
        setScanning(true);
        try {
            const uni = buildUniverseParam();
            const res = await api.get("/api/parabolic-breakouts", {
                params: {
                    limit: Number(limit) || 20,
                    universe: uni,
                    deadline_ms: 12000,
                }, // 12s server cap
                timeout: 45000, // allow enough time for NYSE scan to finish
            });
            setRows(res.data?.parabolicBreakouts || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
            setScanning(false);
        }
    };

    useEffect(() => {
        fetchBreakouts();
    }, []); // initial load

    if (loading)
        return (
            <div className="p-6 text-zinc-300">
                Scanning for early parabolics…
            </div>
        );

    return (
        <div className="p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                <h1 className="text-2xl sm:text-3xl font-bold text-blue-300">
                    🚀 Early Parabolic Breakouts
                </h1>

                {/* Controls */}
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    {/* Universe select */}
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-zinc-400">Universe</label>
                        <select
                            value={universe}
                            onChange={(e) => setUniverse(e.target.value)}
                            className="bg-zinc-800 text-zinc-100 border border-zinc-700 rounded-lg px-3 py-2 text-sm"
                        >
                            <option value="popular">Popular (fast)</option>
                            <option value="nyse">NYSE (wider scan)</option>
                            <option value="custom">Custom list…</option>
                        </select>
                    </div>

                    {/* Custom tickers input */}
                    {universe === "custom" && (
                        <input
                            value={customTickers}
                            onChange={(e) => setCustomTickers(e.target.value)}
                            placeholder="AAPL,TSLA,NVDA"
                            className="bg-zinc-800 text-zinc-100 border border-zinc-700 rounded-lg px-3 py-2 text-sm w-full sm:w-64"
                        />
                    )}

                    {/* Limit */}
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-zinc-400">Limit</label>
                        <input
                            type="number"
                            min="5"
                            max="50"
                            value={limit}
                            onChange={(e) => setLimit(e.target.value)}
                            className="bg-zinc-800 text-zinc-100 border border-zinc-700 rounded-lg px-3 py-2 text-sm w-20"
                        />
                    </div>

                    {/* Scan Now */}
                    <button
                        onClick={fetchBreakouts}
                        disabled={scanning}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold shadow ${scanning
                                ? "bg-blue-900 text-blue-300 cursor-wait"
                                : "bg-blue-600 hover:bg-blue-700 text-white"
                            }`}
                    >
                        {scanning ? "Scanning…" : "🔄 Scan Now"}
                    </button>
                </div>
            </div>

            <p className="text-sm text-zinc-400 mb-4">
                Candidates building energy before a breakout. Click a ticker to
                open the live chart.
            </p>

            <div className="space-y-3">
                {rows.map((r) => (
                    <div
                        key={r.ticker}
                        className="bg-zinc-800 rounded-xl p-4 shadow hover:bg-zinc-700 cursor-pointer"
                        onClick={() => navigate(`/chart/${r.ticker}`)}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                {/* 🔥 Neon animated ticker + name */}
                                <TickerWithName symbol={r.ticker} />
                                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-600/30 text-emerald-300">
                                    {Math.round(r.confidence)}% conf
                                </span>
                            </div>
                            <div className="text-sm text-zinc-300">
                                Score {Math.round(r.score)}
                            </div>
                        </div>

                        <div className="mt-1 text-sm text-zinc-300">
                            Entry ${money2(r.entry)} • Exit ${money2(r.exit)}
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">
                            Reason: {r.reason}
                        </div>

                        {/* Quick stock trade */}
                        <QuickTradeEquityButton ticker={r.ticker} />

                        {r.options?.length > 0 && (
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {r.options.slice(0, 2).map((o, i) => (
                                    <div
                                        key={i}
                                        className="bg-blue-900/40 border border-blue-800 rounded-lg p-3"
                                    >
                                        <div className="text-xs uppercase tracking-wide text-blue-300 mb-1">
                                            {o.strategy} • Exp {o.expiration}
                                        </div>
                                        <div className="text-sm text-blue-100">
                                            Strike ${money2(o.strike)} • Entry $
                                            {money2(o.entryPrice)} • Est Exit $
                                            {money2(o.estimatedExit)}
                                        </div>
                                        <div className="text-xs text-blue-300 mt-1">
                                            {o.reason} •{" "}
                                            {Math.round(o.confidence)}% conf
                                        </div>
                                        <div className="mt-2 flex gap-2">
                                            <QuickTradeOptionButton
                                                ticker={r.ticker}
                                                strategy={o.strategy}
                                                strike={o.strike}
                                                expiration={o.expiration}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}

                {rows.length === 0 && (
                    <div className="text-zinc-400">
                        No strong early breakouts right now. Try a different
                        universe or scan again later.
                    </div>
                )}
            </div>
        </div>
    );
}
